import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { HelpCircle, Plus, RotateCcw, Search, Trash2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ParticipantCard, StoreColumn } from './StoreColumn';
import ConfirmDialog from '../ui/confirm-dialog';
import { TooltipIconButton } from '../ui/tooltip-icon-button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../ui/tooltip';
import {
  addAssignment,
  addStore,
  countAssignedParticipants,
  countParticipantAssignments,
  filterParticipantsForPlanner,
  formatParticipantDisplayName,
  getVisibleParticipants,
  moveAssignment,
  participantMatchesSlot,
  removeAssignment,
  reorderStoreSlots,
} from '../../lib/events';
import { cn } from '../../lib/utils';

function useIsBelowLg() {
  const [isBelow, setIsBelow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 1023px)');
    const handleChange = (event) => setIsBelow(event.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return isBelow;
}

function DraggableParticipant({ participant, assignmentCount, isSelected, onMobileSelect }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `participant:${participant.responseId}`,
    data: { type: 'participant', responseId: participant.responseId },
  });

  return (
    <div ref={setNodeRef} className={cn('w-full min-w-0', isDragging && 'opacity-40')}>
      <ParticipantCard
        participant={participant}
        compact
        showGrip
        selected={isSelected}
        assignmentCount={assignmentCount}
        onSelect={onMobileSelect}
        dragHandleProps={{ ...listeners, ...attributes, className: 'cursor-grab active:cursor-grabbing' }}
      />
    </div>
  );
}

function TrashDropZone({ compact = false }) {
  const { setNodeRef, isOver } = useDroppable({ id: 'trash' });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        'flex items-center justify-center gap-2 rounded-lg border-2 border-dashed text-sm transition-colors',
        compact ? 'px-3 py-2 text-xs' : 'px-4 py-3',
        isOver
          ? 'border-brand-danger bg-brand-danger/10 text-brand-danger'
          : 'border-[var(--ba-surface-border)] text-[var(--ba-muted)]'
      )}
    >
      <Trash2 className={compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
      {compact
        ? 'Trascina qui per rimuovere'
        : 'Trascina qui un volontario già assegnato per annullare quell\'assegnazione'}
    </div>
  );
}

function AssignmentProgress({ assigned, total }) {
  const pct = total > 0 ? Math.round((assigned / total) * 100) : 0;
  return (
    <div className="rounded-xl border border-[var(--ba-surface-border)] bg-[var(--ba-surface-bg)] p-4">
      <div className="mb-2 flex items-center justify-between gap-2 text-sm">
        <span className="font-semibold">Progresso assegnazioni</span>
        <span className="tabular-nums ba-muted">
          {assigned}/{total} partecipanti ({pct}%)
        </span>
      </div>
      <div className="h-2.5 overflow-hidden rounded-full bg-black/[0.06]">
        <div
          className="h-full rounded-full bg-brand-green transition-all duration-300"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function EventPlannerBoard({ event, setEvent, participants }) {
  const [newStoreName, setNewStoreName] = useState('');
  const [activeDrag, setActiveDrag] = useState(null);
  const [showAssigned, setShowAssigned] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchMode, setSearchMode] = useState('participant');
  const [slotFilter, setSlotFilter] = useState('');
  const [mobilePick, setMobilePick] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [pendingTrashAssignmentId, setPendingTrashAssignmentId] = useState(null);
  const [pendingConflict, setPendingConflict] = useState(null);
  const [undoSnapshot, setUndoSnapshot] = useState(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const isMobileLayout = useIsBelowLg();

  const isSearching = searchTerm.trim().length > 0;
  const slots = event.slots || [];
  const assignedCount = countAssignedParticipants(participants, event.assignments);
  const totalParticipants = participants.length;

  const listParticipants = useMemo(() => {
    let base;
    if (isSearching) {
      base = filterParticipantsForPlanner(participants, searchTerm, searchMode, slotFilter);
    } else {
      base = getVisibleParticipants(participants, event.assignments, showAssigned);
      if (slotFilter) {
        base = base.filter((p) =>
          p.availabilitySlots?.some((s) => String(s).toLowerCase() === slotFilter.toLowerCase())
        );
      }
    }
    return base;
  }, [isSearching, participants, searchTerm, searchMode, slotFilter, event.assignments, showAssigned]);

  const unassignedCount = getVisibleParticipants(participants, event.assignments, false).length;

  const findParticipant = useCallback(
    (responseId) => participants.find((p) => p.responseId === responseId),
    [participants]
  );

  const dragParticipant = useMemo(() => {
    if (activeDrag) {
      const data = activeDrag.data.current;
      if (data?.type === 'participant') {
        return findParticipant(data.responseId) || null;
      }
      if (data?.type === 'assignment') {
        const asn = event.assignments.find((a) => a.id === data.assignmentId);
        return asn ? findParticipant(asn.responseId) || null : null;
      }
    }
    if (mobilePick) {
      return findParticipant(mobilePick.responseId) || null;
    }
    return null;
  }, [activeDrag, mobilePick, findParticipant, event.assignments]);

  const applyEventChange = (updater) => {
    setUndoSnapshot(event);
    setEvent(typeof updater === 'function' ? updater : updater);
  };

  const handleUndo = () => {
    if (!undoSnapshot) return;
    setEvent(undoSnapshot);
    setUndoSnapshot(null);
  };

  const commitAssignment = ({ responseId, storeId, slot, type, assignmentId }) => {
    if (type === 'assignment') {
      applyEventChange(moveAssignment(event, assignmentId, storeId, slot));
      return;
    }
    applyEventChange(addAssignment(event, { responseId, storeId, slot }));
  };

  const tryAssignToSlot = ({ responseId, storeId, slot, type, assignmentId }) => {
    const p = findParticipant(responseId);
    if (p && !participantMatchesSlot(p, slot)) {
      setPendingConflict({
        type,
        responseId,
        storeId,
        slot,
        assignmentId,
        label: formatParticipantDisplayName(p),
      });
      return;
    }
    commitAssignment({ responseId, storeId, slot, type, assignmentId });
    setMobilePick(null);
  };

  const handleMobileSlotTap = (storeId, slot) => {
    if (!mobilePick) return;
    tryAssignToSlot({
      responseId: mobilePick.responseId,
      storeId,
      slot,
      type: mobilePick.kind,
      assignmentId: mobilePick.assignmentId,
    });
  };

  const toggleMobileParticipantPick = (responseId) => {
    setMobilePick((current) =>
      current?.kind === 'participant' && current.responseId === responseId
        ? null
        : { kind: 'participant', responseId }
    );
  };

  const toggleMobileAssignmentPick = (assignmentId, responseId) => {
    setMobilePick((current) =>
      current?.kind === 'assignment' && current.assignmentId === assignmentId
        ? null
        : { kind: 'assignment', assignmentId, responseId }
    );
  };

  const handleDragStart = (ev) => {
    setActiveDrag(ev.active);
  };

  const handleDragEnd = (ev) => {
    setActiveDrag(null);
    const { active, over } = ev;
    if (!over) return;

    const activeData = active.data.current;

    if (over.id === 'trash' && activeData?.type === 'assignment') {
      const asn = event.assignments.find((a) => a.id === activeData.assignmentId);
      const p = asn ? findParticipant(asn.responseId) : null;
      setPendingTrashAssignmentId({
        assignmentId: activeData.assignmentId,
        label: p ? formatParticipantDisplayName(p) : 'questo volontario',
      });
      return;
    }

    const overData = over.data.current;

    if (activeData?.type === 'slot-order' && overData?.type === 'slot-order') {
      if (activeData.storeId === overData.storeId) {
        applyEventChange(reorderStoreSlots(event, activeData.storeId, activeData.slot, overData.slot));
      }
      return;
    }

    if (overData?.type !== 'slot') return;

    const { storeId, slot } = overData;

    if (activeData?.type === 'participant') {
      tryAssignToSlot({
        responseId: activeData.responseId,
        storeId,
        slot,
        type: 'participant',
      });
    } else if (activeData?.type === 'assignment') {
      tryAssignToSlot({
        responseId: activeData.responseId,
        storeId,
        slot,
        type: 'assignment',
        assignmentId: activeData.assignmentId,
      });
    }
  };

  const handleRequestRemoveStore = (storeId) => {
    const store = event.stores.find((s) => s.id === storeId);
    if (!store) return;
    setConfirmAction({
      type: 'removeStore',
      title: 'Eliminare il negozio?',
      description: `Stai per eliminare "${store.name}" e tutte le assegnazioni collegate. L'operazione ha effetto sul planner finché non salvi.`,
      confirmLabel: 'Elimina negozio',
      variant: 'destructive',
      onConfirm: () => {
        applyEventChange((current) => ({
          ...current,
          stores: current.stores.filter((s) => s.id !== storeId),
          assignments: current.assignments.filter((a) => a.storeId !== storeId),
        }));
      },
    });
  };

  const handleRequestRemoveAssignment = (assignmentId) => {
    const asn = event.assignments.find((a) => a.id === assignmentId);
    const p = asn ? findParticipant(asn.responseId) : null;
    setConfirmAction({
      type: 'removeAssignment',
      title: 'Rimuovere l\'assegnazione?',
      description: p
        ? `Vuoi rimuovere ${formatParticipantDisplayName(p)} da questo turno?`
        : 'Vuoi annullare questa assegnazione?',
      confirmLabel: 'Rimuovi',
      variant: 'destructive',
      onConfirm: () => applyEventChange((current) => removeAssignment(current, assignmentId)),
    });
  };

  const handleAddStore = () => {
    const name = newStoreName.trim();
    if (!name) return;
    applyEventChange(addStore(event, name));
    setNewStoreName('');
  };

  const activeOverlay = () => {
    if (!activeDrag) return null;
    const data = activeDrag.data.current;
    if (data?.type === 'participant') {
      const p = findParticipant(data.responseId);
      return p ? (
        <ParticipantCard
          participant={p}
          compact
          assignmentCount={countParticipantAssignments(p.responseId, event.assignments)}
        />
      ) : null;
    }
    if (data?.type === 'assignment') {
      const asn = event.assignments.find((a) => a.id === data.assignmentId);
      const p = asn ? findParticipant(asn.responseId) : null;
      return p ? (
        <ParticipantCard
          participant={p}
          compact
          assignmentCount={countParticipantAssignments(p.responseId, event.assignments)}
        />
      ) : null;
    }
    if (data?.type === 'slot-order') {
      return (
        <div className="rounded-lg border border-brand-orange bg-[var(--ba-surface-bg)] px-3 py-2 text-xs font-bold uppercase">
          {data.slot}
        </div>
      );
    }
    return null;
  };

  const participantsPanel = (
    <div className="flex max-h-[70vh] min-h-0 flex-col overflow-hidden rounded-xl border border-[var(--ba-surface-border)] bg-[var(--ba-surface-bg)] shadow-sm lg:sticky lg:top-[5.5rem] lg:max-h-[calc(100vh-6.5rem)] lg:self-start">
      <div className="shrink-0 border-b border-[var(--ba-surface-border)] p-3">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h3 className="text-sm font-bold">
            Partecipanti ({listParticipants.length}{!isSearching && !showAssigned && assignedCount > 0 ? ` / ${totalParticipants}` : ''})
          </h3>
          <div className="flex items-center gap-1">
            {undoSnapshot && (
              <TooltipIconButton
                label="Annulla ultima assegnazione"
                icon={RotateCcw}
                variant="outline"
                onClick={handleUndo}
              />
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="rounded-md p-1 text-[var(--ba-muted)] hover:bg-black/5 hover:text-[var(--ba-body-text)]"
                  aria-label="Aiuto lista partecipanti"
                >
                  <HelpCircle className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="left" className="max-w-xs text-xs">
                Su desktop trascina un partecipante in un turno. Su mobile tocca un partecipante
                e poi il turno desiderato. Dopo l&apos;assegnazione scompare dalla lista a meno che
                non attivi «Mostra già assegnati». I turni compatibili si evidenziano in verde.
              </TooltipContent>
            </Tooltip>
          </div>
        </div>

        <div className="space-y-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--ba-muted)]" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={searchMode === 'participant' ? 'Cerca per nome…' : 'Cerca per turno…'}
              className="h-9 pl-8 pr-8 text-sm"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-[var(--ba-muted)] hover:text-[var(--ba-text)]"
                aria-label="Cancella ricerca"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <div className="flex rounded-md border border-[var(--ba-surface-border)] p-0.5">
            {[
              { id: 'participant', label: 'Nome' },
              { id: 'slot', label: 'Turno' },
            ].map(({ id, label }) => (
              <button
                key={id}
                type="button"
                onClick={() => setSearchMode(id)}
                className={cn(
                  'flex-1 rounded px-2 py-1 text-[11px] font-medium transition-colors',
                  searchMode === id
                    ? 'bg-brand-orange text-white'
                    : 'text-[var(--ba-muted)] hover:text-[var(--ba-text)]'
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {slots.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => setSlotFilter('')}
              className={cn(
                'rounded-full px-2 py-0.5 text-[10px] font-medium transition-colors',
                !slotFilter
                  ? 'bg-brand-orange text-white'
                  : 'border border-[var(--ba-surface-border)] text-[var(--ba-muted)] hover:text-[var(--ba-body-text)]'
              )}
            >
              Tutti
            </button>
            {slots.map((slot) => (
              <button
                key={slot}
                type="button"
                onClick={() => setSlotFilter(slotFilter === slot ? '' : slot)}
                className={cn(
                  'max-w-full truncate rounded-full px-2 py-0.5 text-[10px] font-medium transition-colors',
                  slotFilter === slot
                    ? 'bg-brand-orange text-white'
                    : 'border border-[var(--ba-surface-border)] text-[var(--ba-muted)] hover:text-[var(--ba-body-text)]'
                )}
              >
                {slot}
              </button>
            ))}
          </div>
        )}

        {!isSearching && (
          <label className="mt-3 flex cursor-pointer items-center gap-2 text-xs">
            <input
              type="checkbox"
              className="h-3.5 w-3.5 accent-brand-orange"
              checked={showAssigned}
              onChange={(e) => setShowAssigned(e.target.checked)}
            />
            Mostra partecipanti già assegnati
          </label>
        )}
      </div>

      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
        {listParticipants.length === 0 ? (
          <p className="py-4 text-center text-xs ba-muted">
            {isSearching || slotFilter
              ? 'Nessun partecipante corrisponde ai filtri.'
              : assignedCount === totalParticipants && totalParticipants > 0
                ? 'Tutti assegnati. Attiva il toggle per vederli di nuovo.'
                : 'Nessun partecipante.'}
          </p>
        ) : (
          listParticipants.map((p) => (
            <DraggableParticipant
              key={p.responseId}
              participant={p}
              assignmentCount={countParticipantAssignments(p.responseId, event.assignments)}
              isSelected={mobilePick?.kind === 'participant' && mobilePick.responseId === p.responseId}
              onMobileSelect={
                isMobileLayout ? () => toggleMobileParticipantPick(p.responseId) : undefined
              }
            />
          ))
        )}
      </div>

      <div className="shrink-0 space-y-2 border-t border-[var(--ba-surface-border)] p-3">
        {!isSearching && unassignedCount > 0 && (
          <p className="text-xs text-brand-gold">{unassignedCount} non ancora assegnati</p>
        )}
        <TrashDropZone compact />
      </div>
    </div>
  );

  const boardPanel = (
    <div className="relative min-w-0 max-w-full overflow-hidden">
      <div className="max-w-full overflow-hidden rounded-xl border border-[var(--ba-surface-border)] bg-black/[0.01]">
        {event.stores.length === 0 ? (
          <div className="space-y-3 px-6 py-12 text-center">
            <p className="text-base font-semibold">Inizia dal negozio</p>
            <ol className="mx-auto max-w-xs list-decimal space-y-1 pl-4 text-left text-sm ba-muted">
              <li>Aggiungi un negozio con il campo sopra</li>
              <li>Cerca un partecipante nella lista a sinistra</li>
              <li>Trascinalo nel turno del negozio desiderato</li>
            </ol>
            {dragParticipant && (
              <p className="text-xs text-brand-green">
                Turni in verde = compatibili con la disponibilità del partecipante
              </p>
            )}
          </div>
        ) : (
          <div className="max-w-full overflow-x-auto overscroll-x-contain p-3">
            <div className="flex w-max gap-3">
              {event.stores.map((store) => (
                <StoreColumn
                  key={store.id}
                  store={store}
                  globalSlots={slots}
                  assignments={event.assignments}
                  participants={participants}
                  dragParticipant={dragParticipant}
                  isMobilePickMode={isMobileLayout && !!mobilePick}
                  mobileSelectedAssignmentId={
                    mobilePick?.kind === 'assignment' ? mobilePick.assignmentId : null
                  }
                  onMobileSlotTap={isMobileLayout ? handleMobileSlotTap : undefined}
                  onMobileAssignmentPick={
                    isMobileLayout ? toggleMobileAssignmentPick : undefined
                  }
                  onReorderSlot={(storeId, fromSlot, toSlot) =>
                    applyEventChange(reorderStoreSlots(event, storeId, fromSlot, toSlot))
                  }
                  onUpdateStore={(storeId, patch) =>
                    applyEventChange({
                      ...event,
                      stores: event.stores.map((s) =>
                        s.id === storeId ? { ...s, ...patch } : s
                      ),
                    })
                  }
                  onRemoveStore={handleRequestRemoveStore}
                  onRemoveAssignment={handleRequestRemoveAssignment}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      autoScroll={{ threshold: { x: 0.1, y: 0.15 } }}
    >
      <div className="min-w-0 space-y-4">
        <AssignmentProgress assigned={assignedCount} total={totalParticipants} />

        {isMobileLayout && mobilePick && (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-brand-orange bg-brand-orange/10 px-3 py-2 text-sm">
            <span>
              {mobilePick.kind === 'participant' ? 'Tocca un turno per assegnare' : 'Tocca un turno per spostare'}{' '}
              <strong>{formatParticipantDisplayName(findParticipant(mobilePick.responseId) || {})}</strong>
            </span>
            <Button type="button" variant="outline" size="sm" onClick={() => setMobilePick(null)}>
              Annulla
            </Button>
          </div>
        )}

        <div className="max-w-md">
          <label className="ba-label">Aggiungi negozio</label>
          <div className="flex gap-2">
            <Input
              value={newStoreName}
              onChange={(e) => setNewStoreName(e.target.value)}
              placeholder="Nome negozio…"
              onKeyDown={(e) => e.key === 'Enter' && handleAddStore()}
            />
            <Button type="button" variant="outline" size="icon" onClick={handleAddStore} aria-label="Aggiungi negozio">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="grid min-w-0 gap-4 lg:grid-cols-[300px_minmax(0,1fr)]">
          <div className="min-w-0">{participantsPanel}</div>
          <div className="min-w-0">{boardPanel}</div>
        </div>
      </div>

      <DragOverlay>{activeOverlay()}</DragOverlay>

      <ConfirmDialog
        open={!!confirmAction}
        onOpenChange={(open) => !open && setConfirmAction(null)}
        title={confirmAction?.title}
        description={confirmAction?.description}
        confirmLabel={confirmAction?.confirmLabel}
        variant={confirmAction?.variant}
        onConfirm={confirmAction?.onConfirm}
      />

      <ConfirmDialog
        open={!!pendingTrashAssignmentId}
        onOpenChange={(open) => !open && setPendingTrashAssignmentId(null)}
        title="Annullare l'assegnazione?"
        description={`Vuoi rimuovere ${pendingTrashAssignmentId?.label} da questo turno?`}
        confirmLabel="Rimuovi"
        variant="destructive"
        onConfirm={() => {
          const assignmentId = pendingTrashAssignmentId?.assignmentId;
          if (assignmentId) {
            applyEventChange((current) => removeAssignment(current, assignmentId));
          }
        }}
      />

      <ConfirmDialog
        open={!!pendingConflict}
        onOpenChange={(open) => !open && setPendingConflict(null)}
        title="Turno non in disponibilità"
        description={
          pendingConflict
            ? `${pendingConflict.label} non ha indicato il turno «${pendingConflict.slot}» tra le proprie disponibilità. Vuoi assegnarlo comunque?`
            : ''
        }
        confirmLabel="Assegna comunque"
        onConfirm={() => {
          if (!pendingConflict) return;
          commitAssignment(pendingConflict);
          setPendingConflict(null);
          setMobilePick(null);
        }}
      />
    </DndContext>
  );
}

export default EventPlannerBoard;
